<template>
  <v-container
    v-if="authenticationMixin.checkedLogin"
    class="content content-with-nav pa-0"
    fluid
  >
    <!-- Hero section -->
    <page-hero
      title="Eigen teksten / getuigenissen"
      intro-lg="8"
    >
      <p class="page-hero-intro text-center">
        U kan uw persoonlijke teksten en/of een gedicht uit onze gedichtenbundel toevoegen zodat deze in de uitvaart kunnen opgenomen worden.
      </p>
      <p class="page-hero-intro text-center mt-2">
        Wijzigingen via uw persoonlijk account kunnen tot twee dagen voor de uitvaart.
        Hierna toch nog een wijziging? Neem gerust telefonisch contact met ons op.
      </p>
    </page-hero>

    <!-- Teksten list -->
    <v-row justify="center">
      <v-col
        class="center"
        cols="12"
        md="10"
        lg="8"
      >
        <div class="teksten-card">
          <div class="teksten-card-header">
            <v-icon
              color="#907322"
              size="22"
            >
              mdi-text-box-outline
            </v-icon>
            <h2 class="teksten-card-title">
              Persoonlijke teksten
            </h2>
          </div>

          <template
            v-for="(tekst, index) in data.teksten"
            :key="index"
          >
            <v-form
              v-model="tekst.validForm"
              :ref="`formPersoonlijkeTeksten-${index}`"
            >
              <div
                class="teksten-item"
                :class="{ 'teksten-item--expanded': Expanded(tekst) }"
              >
                <!-- Collapsed header -->
                <div
                  class="teksten-item-header"
                  @click="CollapseOrExpand(tekst)"
                >
                  <div class="teksten-item-header-left">
                    <v-tooltip
                      v-if="!tekst.valid"
                      location="start"
                      text="Niet alle velden zijn correct ingevuld."
                    >
                      <template v-slot:activator="{ props }">
                        <v-icon
                          color="error"
                          size="18"
                          v-bind="props"
                        >
                          mdi-alert-circle
                        </v-icon>
                      </template>
                    </v-tooltip>
                    <v-icon
                      color="#907322"
                      size="18"
                    >
                      mdi-chevron-{{ Expanded(tekst) ? 'down' : 'right' }}
                    </v-icon>
                    <span class="teksten-item-name">
                      {{ tekst.voornaam || tekst.familienaam ? `${tekst.voornaam ?? ''} ${tekst.familienaam ?? ''}` : '-' }}
                    </span>
                  </div>
                  <v-btn
                    v-if="!tekst.idDossierUitvaartTekst || tekst.idDossierUitvaartTekst === constantMixin.guids.empty"
                    color="primary"
                    icon
                    size="x-small"
                    variant="text"
                    @click.stop="DeleteTekst(tekst)"
                  >
                    <v-icon size="18">
                      mdi-close
                    </v-icon>
                  </v-btn>
                </div>

                <!-- Expanded content -->
                <template v-if="Expanded(tekst)">
                  <div class="teksten-item-body">
                    <h3 class="teksten-section-title teksten-section-title-spaced">
                      Deze tekst is van
                    </h3>
                    <v-row>
                      <v-col
                        cols="12"
                        lg="6"
                      >
                        <v-text-field
                          v-model="tekst.familienaam"
                          base-color="primary"
                          bg-color="white"
                          color="primary"
                          density="compact"
                          label="Uw naam *"
                          :rules="[validationMixin.rules.max50Characters, validationMixin.rules.required]"
                          variant="outlined"
                        />
                      </v-col>
                      <v-col
                        cols="12"
                        lg="6"
                      >
                        <v-text-field
                          v-model="tekst.voornaam"
                          base-color="primary"
                          bg-color="white"
                          color="primary"
                          density="compact"
                          label="Uw voornaam *"
                          :rules="[validationMixin.rules.max50Characters, validationMixin.rules.required]"
                          variant="outlined"
                        />
                      </v-col>
                    </v-row>
                    <v-text-field
                      v-model="tekst.relatie"
                      base-color="primary"
                      bg-color="white"
                      color="primary"
                      density="compact"
                      label="Uw relatie tot de overledene *"
                      :rules="[validationMixin.rules.required]"
                      variant="outlined"
                    />

                    <h3 class="teksten-section-title">
                      Tekst uploaden
                    </h3>
                    <control-input
                      v-model="tekst.bestandUitvaartTekst"
                      :accept="['doc', 'docx', 'pdf']"
                      dense
                      label="Toegelaten bestandstypes: doc, docx, pdf"
                      :maxFileSize="2"
                      :maxFileSizeText="textMixin.messages.maxFileSize"
                      outlined
                      @change="tekst.bestandUitvaartTekst.isEdited = true"
                      @clear="BestandClear(tekst)"
                      @download="Download(tekst)"
                      @data="tekst.bestandUitvaartTekst.blobDataString = $event"
                      @name="tekst.bestandUitvaartTekst.bestandsnaam = $event"
                    />

                    <h3 class="teksten-section-title">
                      Tekst ingeven
                    </h3>
                    <v-textarea
                      v-model="tekst.tekst"
                      auto-grow
                      base-color="primary"
                      bg-color="white"
                      color="primary"
                      density="compact"
                      variant="outlined"
                    />

                    <h3 class="teksten-section-title">
                      Gedicht kiezen
                    </h3>
                    <v-text-field
                      v-model="tekst.gedicht.naam"
                      base-color="primary"
                      bg-color="white"
                      color="primary"
                      density="compact"
                      readonly
                      variant="outlined"
                    >
                      <template v-slot:append-inner>
                        <v-btn
                          color="primary"
                          rounded
                          size="x-small"
                          @click="GedichtOpen()"
                        >
                          Kies
                        </v-btn>
                      </template>
                    </v-text-field>

                    <v-textarea
                      v-model="tekst.opmerking"
                      auto-grow
                      base-color="primary"
                      bg-color="white"
                      color="primary"
                      density="compact"
                      label="Plaats hier eventuele extra opmerkingen"
                      variant="outlined"
                    />

                    <h3 class="teksten-section-title">
                      Voorgelezen door *
                    </h3>
                    <v-radio-group
                      v-model="tekst.voorgelezenDoor"
                      inline
                      :rules="[validationMixin.rules.requiredCheckbox]"
                    >
                      <v-radio
                        :value="constantMixin.codes.voorgelezenDoor.values.dePersoonZelf"
                        base-color="primary"
                        color="primary"
                        label="De persoon zelf"
                      />
                      <v-radio
                        :value="constantMixin.codes.voorgelezenDoor.values.deVoorganger"
                        base-color="primary"
                        color="primary"
                        label="De voorganger"
                      />
                    </v-radio-group>
                  </div>
                </template>
              </div>
            </v-form>
          </template>

          <!-- Actions -->
          <div class="teksten-actions">
            <v-btn
              color="primary"
              rounded
              variant="outlined"
              @click="NieuweTekst"
            >
              <v-icon
                class="mr-1"
                size="18"
              >
                mdi-plus
              </v-icon>
              Tekst toevoegen
            </v-btn>
            <v-tooltip
              :disabled="TekstenValid"
              location="top"
              text="Niet alle velden zijn correct ingevuld."
            >
              <template v-slot:activator="{ props }">
                <div v-bind="props">
                  <v-btn
                    color="primary"
                    :disabled="!TekstenValid"
                    rounded
                    @click="SaveTeksten"
                  >
                    <v-icon
                      class="mr-1"
                      size="18"
                    >
                      mdi-content-save-outline
                    </v-icon>
                    Teksten opslaan
                  </v-btn>
                </div>
              </template>
            </v-tooltip>
          </div>
        </div>

        <!-- Volgorde -->
        <div class="teksten-card mt-4 mb-8">
          <div class="teksten-card-header">
            <v-icon
              color="#907322"
              size="22"
            >
              mdi-sort
            </v-icon>
            <h2 class="teksten-card-title">
              Volgorde
            </h2>
          </div>
          <v-select
            v-model="algemeneInfo.tekstenVolgordeBepalingValue"
            base-color="primary"
            bg-color="white"
            color="primary"
            density="compact"
            item-title="longDescription"
            item-value="value"
            :items="tekstenVolgordeOpties"
            label="Wie bepaalt de volgorde van de teksten?"
            variant="outlined"
          />
          <div class="d-flex justify-end">
            <v-btn
              color="primary"
              rounded
              @click="SaveTekstenVolgorde"
            >
              <v-icon
                class="mr-1"
                size="18"
              >
                mdi-content-save-outline
              </v-icon>
              Opslaan
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Gedichtenbundel dialog -->
    <v-dialog
      v-model="dialog"
      width="800"
    >
      <v-card class="teksten-dialog">
        <div class="teksten-dialog-header">
          <h2 class="teksten-dialog-title">
            Gedichtenbundel
          </h2>
          <p class="teksten-dialog-intro">
            U kan een gedichtje kiezen of zich laten inspireren.
          </p>
        </div>
        <v-row class="pa-4">
          <v-col
            v-for="(item, index) in gedichten"
            :key="index"
            class="pa-3"
            :class="{ 'teksten-gedicht--selected': expanded && item.idGedicht === expanded.gedicht.idGedicht }"
            cols="12"
            lg="6"
          >
            <div class="teksten-gedicht">
              <span class="teksten-gedicht-nummer">{{ item.volgnummer }}</span>
              <p
                class="teksten-gedicht-tekst"
                style="white-space: pre-line"
              >
                {{ item.tekst }}
              </p>
              <span class="teksten-gedicht-auteur">{{ item.auteur }}</span>
              <div class="teksten-gedicht-footer">
                <div class="teksten-gedicht-line" />
                <v-btn
                  color="primary"
                  :disabled="expanded && item.idGedicht === expanded.gedicht.idGedicht"
                  rounded
                  size="small"
                  @click="GedichtSelecteer(item)"
                >
                  Kies
                </v-btn>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>

    <!-- Navigation -->
    <div class="samenstellen-nav">
      <div class="samenstellen-nav-inner">
        <v-btn
          v-if="samenstellenNavigation_PreviousItem"
          color="primary"
          :disabled="samenstellenNavigation_PreviousItem.disabled"
          rounded
          variant="text"
          @click="samenstellenNavigation_GoToPrevious"
        >
          <v-icon start>
            mdi-chevron-left
          </v-icon>
          {{ samenstellenNavigation_PreviousItem.title }}
        </v-btn>
        <span v-else />
        <v-btn
          v-if="samenstellenNavigation_NextItem"
          color="primary"
          :disabled="samenstellenNavigation_NextItem.disabled"
          rounded
          variant="text"
          @click="samenstellenNavigation_GoToNext"
        >
          {{ samenstellenNavigation_NextItem.title }}
          <v-icon end>
            mdi-chevron-right
          </v-icon>
        </v-btn>
        <span v-else />
      </div>
    </div>
  </v-container>
</template>

<script>
import PageHero from '@/components/PageHero.vue'
import authenticationMixin from '@/mixins/authenticationMixin'
import blobMixin from '@/mixins/blobMixin'
import commonMixin from '@/mixins/commonMixin'
import constantMixin from '@/mixins/constantMixin'
import errorMixin from '@/mixins/errorMixin'
import samenstellenNavigationMixin from '@/mixins/samenstellenNavigationMixin'
import validationMixin from '@/mixins/validationMixin'
import ControlInput from '../ControlInput.vue'
import blobService from '@/services/blobService'
import klantService from '@/services/klantService'

export default {
  name: 'TekstenComponent',
  components: {
    ControlInput,
    PageHero
  },
  mixins: [
    authenticationMixin,
    blobMixin,
    commonMixin,
    constantMixin,
    errorMixin,
    samenstellenNavigationMixin,
    validationMixin
  ],
  data () {
    return {
      dialog: false,
      expandAll: false,
      expanded: null,
      gedichten: [],
      lastSavedData: null,
      nieuweTekst: null,
      algemeneInfo: {},
      tekstenVolgordeOpties: [],
      data: {
        teksten: []
      }
    }
  },
  beforeRouteLeave (to, from, next) {
    if (this.isDirty) {
      if (this.CommonMixin_Confirmation(true, this.textMixin.messages.unsavedChanges, true)) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
  async mounted () {
    window.scrollTo({ top: 0, behavior: 'smooth' })
    this.AuthenticationMixin_CheckIfLoggedIn()
    this.$store.state.headers.samenstellen = true
    blobService.Init(this)
    klantService.Init(this)
    await this.GetGedichtList()
    await this.GetTekstenList()
    await this.GetTekstenDetail()
    await this.GetTekstenVolgordeOpties()
    this.GetTekstenVolgorde()
  },
  beforeUnmount () {
    this.$store.state.headers.samenstellen = false
  },
  watch: {
    'expanded.validForm' (value) {
      if (this.expanded) {
        this.expanded.valid = value
      }
    }
  },
  computed: {
    isDirty () {
      return this.lastSavedData !== null && !this.CommonMixin_HardCompare(this.data, this.lastSavedData)
    },
    TekstenValid () {
      return this.data.teksten.every(tekst => tekst.valid)
    }
  },
  methods: {
    BestandClear (tekst) {
      tekst.bestandUitvaartTekst.blobDataString = null
      tekst.bestandUitvaartTekst.bestandsnaam = null
    },
    CollapseOrExpand (tekst) {
      if (this.expanded === tekst) {
        this.expanded = null
      } else {
        this.expanded = tekst
      }
    },
    DeleteTekst (tekst) {
      if (this.CommonMixin_Confirmation(!tekst.idDossierUitvaartTekst || tekst.idDossierUitvaartTekst === this.constantMixin.guids.empty, this.textMixin.messages.deleteConfirmation.replaceAll('@@item', 'deze tekst'), false)) {
        const index = this.data.teksten.indexOf(tekst)
        if (index > -1) {
          this.data.teksten.splice(index, 1)
        }
      }
    },
    async Download (tekst) {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await blobService.Download(tekst.bestandUitvaartTekst.idBlob)

      if (resp && (resp.data || resp.data !== '')) {
        this.BlobMixin_DownloadAction(tekst.bestandUitvaartTekst.bestandsnaam, null, resp.data)
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    Expanded (tekst) {
      return this.expandAll || this.expanded === tekst
    },
    GedichtOpen () {
      this.dialog = true
    },
    GedichtSelecteer (gedicht) {
      this.expanded.gedicht = gedicht
      this.expanded.idGedicht = gedicht.idGedicht
      this.dialog = false
    },
    async GetGedichtList () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetUitvaartGedichtList()

      if (resp && (resp.data || resp.data !== '')) {
        this.gedichten = this.CommonMixin_Clone(resp.data)
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    async GetTekstenVolgordeOpties () {
      const resp = await klantService.GetUitvaartDraaiboekListCode()

      if (resp?.data) {
        this.tekstenVolgordeOpties = resp.data.filter(o => o.type === this.constantMixin.codes.tekstenVolgorde.name)
      }
    },
    async GetTekstenVolgorde () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetUitvaartTekstenVolgorde()

      if (resp && (resp.data || resp.data !== '')) {
        this.algemeneInfo = resp.data
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    async SaveTekstenVolgorde () {
      this.CommonMixin_Load(this.$options.name, true)
      await klantService.SaveUitvaartTekstenVolgorde(this.algemeneInfo)
      this.CommonMixin_Load(this.$options.name, false)
    },
    async GetTekstenDetail () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetUitvaartTekstenDetail(this.constantMixin.guids.empty)

      if (resp && (resp.data || resp.data !== '')) {
        this.nieuweTekst = this.CommonMixin_Clone(resp.data)
        this.$forceUpdate()
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    async GetTekstenList () {
      this.CommonMixin_Load(this.$options.name, true)
      const resp = await klantService.GetUitvaartTekstenList()

      if (resp && (resp.data || resp.data !== '')) {
        this.data = this.CommonMixin_Clone(resp.data)
        this.lastSavedData = this.CommonMixin_Clone(this.data)
        this.$forceUpdate()
        await this.ValidateTeksten()
      }

      this.CommonMixin_Load(this.$options.name, false)
    },
    NieuweTekst () {
      this.data.teksten.push(this.CommonMixin_Clone(this.nieuweTekst))
      this.$forceUpdate()
    },
    async SaveTeksten () {
      await this.ValidateTeksten()
      if (this.TekstenValid) {
        this.CommonMixin_Load(this.$options.name, true)
        await klantService.SaveUitvaartTeksten(this.data)
        this.CommonMixin_Load(this.$options.name, false)
        await this.GetTekstenList()
      }
    },
    async ValidateTekst (tekst, index) {
      const validationResult = await this.$refs[`formPersoonlijkeTeksten-${index}`][0].validate()
      tekst.valid = validationResult.valid
    },
    async ValidateTeksten () {
      this.expandAll = true
      setTimeout(() => {
        this.data.teksten.forEach(async (tekst, index) => {
          await this.ValidateTekst(tekst, index)
          this.expandAll = false
        })
      }, 1)
    }
  }
}
</script>

<style scoped>
.teksten-card {
  border: 1px solid #e8e4dc;
  border-radius: 10px;
  padding: 24px;
  background-color: #fdfcfa;
}

.teksten-card-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-bottom: 16px;
  margin-bottom: 16px;
  border-bottom: 2px solid #DEC067;
}

.teksten-card-title {
  font-size: 20px;
  font-weight: 500;
  color: #424E58;
  padding: 0;
  margin: 0;
}

.teksten-section-title {
  font-size: 14px;
  font-weight: 600;
  color: #907322;
  margin: 8px 0;
  padding: 0;
}

.teksten-section-title-spaced {
  margin-bottom: 16px;
}

/* Collapsible items */
.teksten-item {
  border: 1px solid #e8e4dc;
  border-radius: 8px;
  margin-bottom: 8px;
  background-color: white;
  overflow: hidden;
}

.teksten-item--expanded {
  border-color: #DEC067;
}

.teksten-item-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.teksten-item-header:hover {
  background-color: #FBF7EE;
}

.teksten-item-header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.teksten-item-name {
  font-size: 15px;
  font-weight: 500;
  color: #424E58;
}

.teksten-item-body {
  padding: 0 16px 16px;
  border-top: 1px solid #f0ede6;
}

/* Actions */
.teksten-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 16px;
}

/* Dialog */
.teksten-dialog {
  border-radius: 10px !important;
}

.teksten-dialog-header {
  padding: 24px 24px 0;
  text-align: center;
}

.teksten-dialog-title {
  font-size: 22px;
  font-weight: 500;
  color: #907322;
  margin: 0;
}

.teksten-dialog-intro {
  font-size: 14px;
  color: #6b6b6b;
  margin-top: 8px;
}

.teksten-gedicht {
  padding: 16px;
}

.teksten-gedicht--selected {
  background-color: #FBF7EE;
  border-radius: 8px;
}

.teksten-gedicht-nummer {
  font-size: 18px;
  font-weight: 600;
  color: #907322;
}

.teksten-gedicht-tekst {
  font-size: 14px;
  color: #424E58;
  margin-top: 8px;
}

.teksten-gedicht-auteur {
  font-size: 13px;
  font-style: italic;
  color: #6b6b6b;
}

.teksten-gedicht-footer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.teksten-gedicht-line {
  flex: 1;
  border-bottom: 1px solid #e8e4dc;
}

@media (max-width: 960px) {
  .teksten-actions {
    flex-direction: column;
  }
}
</style>
